import { useEffect, useMemo } from 'react';
import {
	AMM_RESERVE_PRECISION,
	AMM_RESERVE_PRECISION_EXP,
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	ONE,
	PerpPosition,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	SpotMarketConfig,
	ZERO,
} from '@velocity-exchange/sdk';
import { ZERO_PRICE_BIGNUM } from 'src/constants/math';
import {
	useSetTradeFormBaseSize,
	useSetTradeFormQuoteSize,
} from './useSetTradeFormInputState';
import {
	UIMarket,
	UIOrderType,
	getBigNumRoundedToStepSize,
} from '@velocity-exchange/common';
import useSyncedRef from 'src/hooks/utils/useSyncedRef';

interface Params {
	oraclePrice: BigNum | undefined;
	markPrice: BigNum | null;
	orderType: UIOrderType;
	priceBoxStringValue: string;
	secondaryPriceBoxStringValue: string;
	leadSide: 'quote' | 'base';
	baseSizeStringValue: string;
	quoteSizeStringValue: string;
	stepSize: number;
	tradeSide: 'buy' | 'sell';
	currentPosition: PerpPosition | undefined;
	selectedMarket: UIMarket;
}

/**
 * Hook that handles updating size values based on price changes.
 *
 * This hook manages the complex logic of calculating base and quote sizes based on
 * different order types, market conditions, and user inputs. It automatically updates
 * the form values when the user leads with either base or quote size.
 *
 * Key features:
 * - Handles different order types (market, limit, oracle limit, stop orders)
 * - Calculates position flipping scenarios with proper quote/base adjustments
 * - Synchronizes form values based on which field the user is actively editing
 * - Respects step size precision for base amounts
 */
export const useTradeFormSyncSizeValues = ({
	oraclePrice,
	markPrice,
	orderType,
	priceBoxStringValue,
	secondaryPriceBoxStringValue,
	leadSide,
	baseSizeStringValue,
	quoteSizeStringValue,
	stepSize,
	tradeSide,
	currentPosition,
	selectedMarket,
}: Params) => {
	const setQuoteFormValue = useSetTradeFormQuoteSize();
	const setBaseFormValue = useSetTradeFormBaseSize();

	const basePrecisionToUse = selectedMarket.isPerp
		? BASE_PRECISION_EXP
		: (selectedMarket.market as SpotMarketConfig).precisionExp;

	const rawBaseValue = useMemo(
		() => BigNum.fromPrint(baseSizeStringValue, basePrecisionToUse),
		[baseSizeStringValue, basePrecisionToUse]
	);

	// Use refs for these so the size doesn't auto-update from them changing.
	const markPriceRef = useSyncedRef(markPrice);
	const oraclePriceRef = useSyncedRef(oraclePrice);
	const leadSideRef = useSyncedRef(leadSide);

	useEffect(() => {
		// if no oracle price, skip conversion and clear the size inputs
		if (!oraclePriceRef.current) {
			if (leadSideRef.current === 'quote') {
				setBaseFormValue('');
			} else {
				setQuoteFormValue('');
			}
			return;
		}

		const isUserPriceDependentOrderType =
			orderType === 'limit' ||
			orderType === 'stopLimit' ||
			orderType === 'stopMarket' ||
			orderType === 'takeProfitLimit' ||
			orderType === 'takeProfitMarket' ||
			orderType === 'scaledOrders';

		const shouldUseTradeFormPrice =
			oraclePriceRef.current?.eqZero() || isUserPriceDependentOrderType;

		const tradeFormPriceStringValueToUse =
			// If the order type is a stop limit or take profit limit, we should use the
			// secondary price box value, as that's the price the size is dependent on.
			orderType == 'stopLimit' || orderType == 'takeProfitLimit'
				? secondaryPriceBoxStringValue
				: priceBoxStringValue;

		const useDisplayPriceForQuoteDisplay =
			isUserPriceDependentOrderType && !!tradeFormPriceStringValueToUse;

		// We early return if we:
		//   - are not using the trade form price and we don't have a best bid ask to use for a price,
		//   - are using the trade form price but the user hasn't entered a price,
		//   - the quote size and base size are both empty.
		if (
			(!shouldUseTradeFormPrice && markPriceRef.current?.eqZero()) ||
			(shouldUseTradeFormPrice && !tradeFormPriceStringValueToUse) ||
			(quoteSizeStringValue === '' && baseSizeStringValue === '')
		) {
			return;
		}

		// This is the price we're going to base the rest of our calculations on.
		let priceToUse: BigNum;

		// Set price to use
		if (orderType === 'market') {
			priceToUse = markPriceRef.current?.gtZero()
				? markPriceRef.current
				: oraclePriceRef.current;
		} else if (orderType === 'oracleLimit') {
			// If it's an oracle limit, we calculate the user's offset from the oracle price.
			const priceWithOffset = oraclePriceRef.current?.add(
				BigNum.fromPrint(priceBoxStringValue, PRICE_PRECISION_EXP)
			);

			if (priceWithOffset.lteZero()) {
				// Just setting this to zero, ideally we would show some kind of error if the
				// user's offset results in a negative price.
				priceToUse = ZERO_PRICE_BIGNUM;
			} else {
				priceToUse = priceWithOffset;
			}
		} else {
			priceToUse =
				(shouldUseTradeFormPrice
					? BigNum.fromPrint(
							tradeFormPriceStringValueToUse,
							PRICE_PRECISION_EXP
						)
					: markPriceRef.current) ?? ZERO_PRICE_BIGNUM;
		}

		/**
		 * Handles the logic for the trade form if the user is leading with the quote size of the trade
		 */
		const handleQuoteLeadSide = () => {
			if (priceToUse.eqZero() || quoteSizeStringValue === '') {
				setBaseFormValue('');
				return;
			}

			let newBaseSize = BigNum.fromPrint(
				quoteSizeStringValue,
				QUOTE_PRECISION_EXP
			)
				.shift(AMM_RESERVE_PRECISION_EXP)
				.div(priceToUse);

			let stepSizePrecision = new BN(1 / (stepSize || 1));
			stepSizePrecision = stepSizePrecision.eq(ZERO) ? ONE : stepSizePrecision;

			const roundToStepSizePrecision =
				AMM_RESERVE_PRECISION.div(stepSizePrecision);

			newBaseSize = getBigNumRoundedToStepSize(
				newBaseSize,
				roundToStepSizePrecision
			);

			setBaseFormValue(newBaseSize.toTradePrecision());
		};

		/**
		 * Handles the logic for the trade form if the user is leading with the base size of the trade
		 */
		const handleBaseLeadSide = () => {
			if (priceToUse.eqZero() || baseSizeStringValue === '') {
				setQuoteFormValue('');
				return;
			}

			const priceToUseForQuoteCalculation = useDisplayPriceForQuoteDisplay
				? BigNum.fromPrint(tradeFormPriceStringValueToUse, PRICE_PRECISION_EXP)
				: priceToUse;

			const newQuoteSize = priceToUseForQuoteCalculation.mul(rawBaseValue);
			const newQuoteSizeStringValue = newQuoteSize.toNum().toFixed(2);

			// avoid infinite loops
			if (newQuoteSizeStringValue === quoteSizeStringValue) {
				return;
			}

			setQuoteFormValue(newQuoteSizeStringValue);
		};

		if (leadSideRef.current === 'quote') {
			handleQuoteLeadSide();
		} else {
			handleBaseLeadSide();
		}
	}, [
		priceBoxStringValue,
		secondaryPriceBoxStringValue,
		baseSizeStringValue,
		quoteSizeStringValue,
		orderType,
		stepSize,
		tradeSide,
		currentPosition,
		setBaseFormValue,
		setQuoteFormValue,
		rawBaseValue,
		basePrecisionToUse,
	]);
};
